import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

/**
 * Renders event descriptions written as Markdown. Everything is sanitised down
 * to a short allow-list so a pasted <script> or onclick can never reach a
 * visitor's browser. Images come from links only — we host nothing here.
 */
const schema = {
  ...defaultSchema,
  tagNames: [
    "h1",
    "h2",
    "h3",
    "p",
    "strong",
    "em",
    "ul",
    "ol",
    "li",
    "a",
    "br",
    "img",
    "code",
    "blockquote",
  ],
  attributes: {
    a: ["href", "title"],
    img: ["src", "alt", "title"],
  },
  protocols: {
    href: ["http", "https", "mailto"],
    src: ["http", "https"],
  },
};

export function RichText({ value, className = "" }: { value: string; className?: string }) {
  if (!value?.trim()) return null;
  return (
    <div className={`space-y-3 text-muted-foreground ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        components={{
          h1: ({ children }) => (
            <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">
              {children}
            </h3>
          ),
          h2: ({ children }) => (
            <h4 className="font-display text-xl font-bold uppercase tracking-wide text-foreground">
              {children}
            </h4>
          ),
          h3: ({ children }) => (
            <h5 className="font-display text-base font-bold uppercase tracking-wide text-foreground">
              {children}
            </h5>
          ),
          p: ({ children }) => <p className="whitespace-pre-line">{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-primary/50 pl-3 italic">{children}</blockquote>
          ),
          code: ({ children }) => (
            <code className="rounded bg-muted px-1 py-0.5 text-xs">{children}</code>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer nofollow"
              className="text-primary underline underline-offset-4"
            >
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <img
              src={typeof src === "string" ? src : ""}
              alt={alt ?? ""}
              loading="lazy"
              className="max-h-80 w-auto max-w-full rounded-lg border border-border"
            />
          ),
        }}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
}

export default RichText;
